import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import type { RoundName } from "../../domain/round/types";
import type { RoundWorkspacePayload } from "../../lib/api";
import { exportOperationalScoringPng, exportOperationalTransportPng } from "../reports/operational-export";

type ExportKind = "scoring" | "arrival" | "return";

function endpoint(tournamentId: string, round: RoundName, jobId: string, proposal: string) {
  if (jobId && /^\d+$/.test(proposal)) {
    return `/api/internal/discord/positions/${encodeURIComponent(tournamentId)}/jobs/${encodeURIComponent(jobId)}/proposal/${proposal}/export-workspace?round=${encodeURIComponent(round)}`;
  }
  return `/api/internal/discord/positions/${encodeURIComponent(tournamentId)}/${round}/export-workspace`;
}

export function DiscordDirectExportPage() {
  const [params] = useSearchParams();
  const tournamentId = String(params.get("tournament") || "").trim();
  const rawRound = String(params.get("round") || "R1").toUpperCase();
  const round = (["R1", "R2", "R3", "R4"].includes(rawRound) ? rawRound : "R1") as RoundName;
  const jobId = String(params.get("job") || "").trim();
  const proposal = String(params.get("proposal") || "").trim();

  const [workspace, setWorkspace] = useState<RoundWorkspacePayload | null>(null);
  const [tournamentName, setTournamentName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState<ExportKind | "">("");

  useEffect(() => {
    let cancelled = false;
    setWorkspace(null);
    setTournamentName("");
    setError("");
    if (!tournamentId) {
      setError("Brak turnieju.");
      return;
    }
    void fetch(endpoint(tournamentId, round, jobId, proposal), { headers: { Accept: "application/json" } })
      .then(async (response) => {
        const body = await response.json().catch(() => null);
        if (!response.ok) throw new Error(String(body?.detail?.message || body?.detail || `HTTP ${response.status}`));
        return body;
      })
      .then((body) => {
        if (cancelled) return;
        const next = body?.workspace as RoundWorkspacePayload | undefined;
        if (!next) throw new Error("Brak workspace do eksportu.");
        setWorkspace(next);
        setTournamentName(String(next.tournament?.name || body?.tournament?.name || tournamentId));
      })
      .catch((exc) => {
        if (!cancelled) setError(exc instanceof Error ? exc.message : String(exc));
      });
    return () => { cancelled = true; };
  }, [tournamentId, round, jobId, proposal]);

  async function run(kind: ExportKind) {
    if (!workspace || !tournamentName) return;
    setError("");
    setBusy(kind);
    try {
      if (kind === "scoring") await exportOperationalScoringPng(workspace, tournamentName);
      if (kind === "arrival") await exportOperationalTransportPng(workspace, tournamentName, "to-course");
      if (kind === "return") await exportOperationalTransportPng(workspace, tournamentName, "return");
    } catch (exc) {
      setError(exc instanceof Error ? exc.message : String(exc));
    } finally {
      setBusy("");
    }
  }

  if (error && !workspace) return <main data-error="1">{error}</main>;
  if (!workspace) return <main>Wczytuję eksport…</main>;

  return (
    <main data-ready="1" data-busy={busy ? "1" : "0"} style={{ padding: 24, fontFamily: "Inter, Arial, sans-serif" }}>
      <h1>{tournamentName} · {round}</h1>
      {error ? <p data-export-error="1">{error}</p> : null}
      <button data-export="scoring" disabled={!workspace.schedule || Boolean(busy)} onClick={() => void run("scoring")}>Scoring PNG</button>
      <button data-export="arrival" disabled={!workspace.transport?.departures?.length || Boolean(busy)} onClick={() => void run("arrival")}>Dojazd PNG</button>
      <button data-export="return" disabled={!workspace.transport?.returns?.length || Boolean(busy)} onClick={() => void run("return")}>Powrót PNG</button>
    </main>
  );
}